import { Box } from "@chakra-ui/layout"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useRecoilValue } from "recoil"

import {
  HIDE_PLAYER_CONTROLLER_TIMEOUT,
  MODAL_Z_INDEX,
  USE_MOBILE_UI
} from "../../../constants"
import { DOMEvents } from "../../../constants/domEvents"
import { ElementId, PRODUCT_DESTINATION } from "../../../constants/enums"
import { useBrowserType } from "../../../hooks/useBrowserType"
import { useResizeObserver } from "../../../hooks/useResizeObserver"
import layoutStates from "../../../recoil/atoms/layout"
import { locationOfProductPageAtom } from "../../../recoil/atoms/product-overlay"
import { brandingSelector } from "../../../recoil/atoms/room"
import { findSideRatio } from "../../../util/findSideRatio"
import { waitForElm } from "../../../util/waitForElm"
import { ProductOverlay } from "../../servicesPanel/badges/product-selector/overlay-product"
import { enableDragForFloatingVideo } from "./enableDragForFloatingVideo"
import {
  enableResizeForFloatingVideo,
  LG_FlOATING_WINDOW_SIZE,
  SM_FLOATING_WINDOW_SIZE
} from "./enableResizeForFloatingVideo"
import MiniPlayerButton from "./MiniPlayerButton"
import { sendDragEventsToParent } from "./sendDragEventsToParent"

// Send default floating video size depending on device
export const getDefaultFloatingVideoSize = (isSmallDevice: boolean) => {
  return isSmallDevice
    ? {
        width: SM_FLOATING_WINDOW_SIZE.width,
        height: SM_FLOATING_WINDOW_SIZE.height,
        widthPx: `${SM_FLOATING_WINDOW_SIZE.width}px`,
        heightPx: `${SM_FLOATING_WINDOW_SIZE.height}px`
      }
    : {
        width: LG_FlOATING_WINDOW_SIZE.width,
        height: LG_FlOATING_WINDOW_SIZE.height,
        widthPx: `${LG_FlOATING_WINDOW_SIZE.width}px`,
        heightPx: `${LG_FlOATING_WINDOW_SIZE.height}px`
      }
}

const ProductMainWindow = ({ isMobile, width = null, isLandscape = null }) => {
  const productScreenRef = useRef<HTMLDivElement>(null)
  const roomStyles = useRecoilValue(brandingSelector)
  const [productWidth, productHeight] = useResizeObserver(productScreenRef)
  const prodWidthMainScreen = useMemo(
    () => findSideRatio(productHeight, 16, 9),
    [productHeight]
  )

  const prodHeightMainScreen = useMemo(
    () => findSideRatio(productWidth, 9, 16),
    [productWidth]
  )

  return (
    <Box
      w={width}
      ref={productScreenRef}
      height={prodHeightMainScreen}
      minH={prodHeightMainScreen}
      position={isLandscape ? "unset" : "relative"}
      backgroundColor={
        isMobile ? "transparent" : roomStyles?.alertBackgroundColor ?? "black"
      }
      borderRadius={"0.375rem"}
    >
      <div
        style={{
          position: "absolute",
          bottom: isMobile ? "0px" : "16px",
          zIndex: 1000000,
          left: isLandscape && "50%",
          transform: isLandscape && "translate(-50%)"
        }}
      >
        <ProductOverlay
          width={prodWidthMainScreen}
          height={prodHeightMainScreen}
          // isWithoutTitle={true}
        />
      </div>
    </Box>
  )
}

const FloatingContainer = ({
  children,
  height,
  width,
  minHeight = null,
  isMobile = false,
  renderChildren = null,
  renderProductOverlay = null,
  productProps = {},
  ...props
}) => {
  const { isAndroidMobile } = useBrowserType()
  const containerRef = useRef<HTMLDivElement>(null)
  const fullScreenWrapperRef = useRef<HTMLDivElement>(null)
  const productPageLocation = useRecoilValue(locationOfProductPageAtom)
  const isFloatingDivActive = useRecoilValue(layoutStates.isFloatingDivActive)
  const [showMiniPlayerButton, setShowMiniPlayerButton] = useState(false)

  useEffect(() => {
    const container = containerRef.current
    let clearUpFunction = null
    let cleanUpResizeFunctions = null

    if (isFloatingDivActive) {
      clearUpFunction = enableDragForFloatingVideo(container)
      cleanUpResizeFunctions = enableResizeForFloatingVideo(
        container,
        fullScreenWrapperRef.current,
        isMobile
      )
    
      // send drag event to parent for all device except android
      if (
        productPageLocation === PRODUCT_DESTINATION.PARENT_IFRAME &&
        !isAndroidMobile
      ) {
        clearUpFunction = sendDragEventsToParent(container)
        container.style.left = "0px"
      }

    } else {
      if (container) {
        container.style.left = null
        container.style.top = null
      }
    }

    return () => {
      if (container) {
        clearUpFunction?.()
        cleanUpResizeFunctions?.()
        container.style.left = null
        container.style.top = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFloatingDivActive])

  useEffect(() => {
    const container = containerRef.current
    let timerId = null
    if (!container) return

    if (isFloatingDivActive) {
      timerId = setTimeout(() => {
        // Style applied to MVW, if Product is open on livesell iframe
        const floatingVideoSize = getDefaultFloatingVideoSize(isMobile)
        container.style.width = floatingVideoSize.widthPx
        container.style.height = floatingVideoSize.heightPx
  
        container.style.display = null
        container.style.zIndex = MODAL_Z_INDEX * 10 + ""
        container.style.position = "absolute"
        container.style.left = "6px"
        container.style.bottom = isMobile ? "124px" : "6px"
  
        container.style.borderRadius = "4px"
        container.style.overflow = "hidden"
      }, 500)
    } else {
      container.style.zIndex = null
      container.style.position = null
      container.style.left = null
      container.style.bottom = null
      container.style.width = null
      container.style.height = null
      container.style.borderRadius = null
      container.style.overflow = null
      container.style.minHeight = null
    }

    return () => {
      timerId && clearTimeout(timerId)
    }
  }, [isFloatingDivActive])

  // This method will fade in and out all on video overlays when miniplayer is active
  // const hideOnVideoOverlays = useCallback(async (isMiniplayerActive) => {

  //   console.log("hideOnVideoOverlays", isMiniplayerActive)

  //   const SHOW_OVERLAY_TIMER = 1200
  //   // tab overlay - chat, shop list
  //   const onVideoOverlayContainer = document.getElementById(ElementId.ON_WHOLESCREEN_VIDEO_CONTAINER)
  //   // Feature overlay
  //   const badgeElement = (await waitForElm("fade-badge")) as HTMLDivElement
  //   const overlayElement = document.getElementById(
  //     "leave-overlay-container"
  //   )

  //   if(isMiniplayerActive) {
  //     // hide the overlays
  //     // onVideoOverlayContainer?.classList.add("hidden")
  //     // badgeElement.classList.add("fade")
  //     if(onVideoOverlayContainer)
  //       onVideoOverlayContainer.style.opacity = "0"
  //     badgeElement.style.transition = "none"
  //     badgeElement.style.opacity = "0"

  //   } else {
  //     // show the overlay after 1000 secs
  //     // badgeElement.style.transition = "opacity 0.5s"

  //     setTimeout(() => {
  //       // onVideoOverlayContainer?.classList.remove("hidden")
  //       if(onVideoOverlayContainer)
  //       onVideoOverlayContainer.style.opacity = null
  //       badgeElement.classList.remove("fade")
  //       badgeElement.style.opacity = null
  //       badgeElement.style.transition = null
  //     }, SHOW_OVERLAY_TIMER)

  //   }
  // }, [])

  /** UseEffect use to show and hide miniplayer buttons and hide and show on video overlays */
  useEffect(() => {
    let timerId = null
    const container = containerRef.current

    // hideOnVideoOverlays(isFloatingDivActive)

    const startMiniPlayerHideTimer = () => {
      timerId = setTimeout(() => {
        setShowMiniPlayerButton(false)
      }, HIDE_PLAYER_CONTROLLER_TIMEOUT)
    }

    // Toggle the button visibility when user click
    const handleTouchStartListener = () => {
      timerId && clearTimeout(timerId)
      setShowMiniPlayerButton(prevValue => {
        const value = !prevValue
        if (value) {
          // Start the timer, if true
          startMiniPlayerHideTimer()
        }
        // return toggle value
        return value
      })
    }

    if (isFloatingDivActive) {
      setShowMiniPlayerButton(true)

      if (USE_MOBILE_UI) {
        // hide the miniplayer button after few seconds
        startMiniPlayerHideTimer()
      }

      // Add the touch listener to show miniplayer button
      container.addEventListener(DOMEvents.TOUCHSTART, handleTouchStartListener)
    }

    return () => {
      //CleanUp -> Hide the mini-player buttons, clear the timer and remove the listener
      setShowMiniPlayerButton(false)
      timerId && clearTimeout(timerId)
      container.removeEventListener(
        DOMEvents.TOUCHSTART,
        handleTouchStartListener
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFloatingDivActive])

  const miniPlayerButtonClass = `fade-in-out ${
    showMiniPlayerButton ? "visible" : "hidden"
  }`

  return (
    <>
      {productPageLocation && (
        <ProductMainWindow isMobile={isMobile} {...productProps} />
      )}

      <Box
        id="main-mvw-container"
        style={{ scrollBehavior: "smooth", userSelect: "none" }}
        ref={containerRef}
        height={height}
        width={width}
        cursor={productPageLocation ? "move" : null}
        minH={!productPageLocation && minHeight}
        {...props}
      >
        {isFloatingDivActive && (
          <MiniPlayerButton className={miniPlayerButtonClass} />
        )}
        {children}
      </Box>


      {/* Full screen Wrapper: use for resizing */}
      <Box
        ref={fullScreenWrapperRef}
        position="absolute"
        display="none"
        top="0px"
        left="0px"
        height="100vh"
        width="100vw"
        background="transparent"
        zIndex="2147483647"
      ></Box>
    </>
  )
}

export default FloatingContainer
